import { toLowerCamelCase } from 'utils/string';
import { toast } from 'react-toastify';
import i18next from 'i18next';

import type { ModulesAndAgendasState } from '../index';
import { findSectionIndex, findTabIndex } from 'utils/section/tabSection';
import { recalcSectionItems } from 'utils/section/items';
import type { FormItemDefinition, FormItemType } from 'types';
import type { FormItemDefaultValue } from 'types/formItem';
import type { TranslationName } from 'types/translator';

interface Payload<T extends FormItemType> {
  name: string;
  title: string;
  translatesTitle?: TranslationName[];
  type: T;
  definition: FormItemDefinition[T];
  focusedSectionName: string | null;
  position: {
    x: string;
    y: string;
  };
  edit?: boolean;
  id?: string;
  currentTab: number;
  currentSubTab: number;
  isActionInput?: boolean;
  isParentInput?: boolean;
  defaultValue?: FormItemDefaultValue;
}

export const updateSectionItem = <T extends FormItemType>(
  state: ModulesAndAgendasState,
  {
    payload,
  }: {
    payload: Payload<T>;
  }
) => {
  const {
    name,
    title,
    translatesTitle,
    type,
    definition,
    focusedSectionName,
    position,
    edit,
    id,
    currentTab,
    currentSubTab,
    isActionInput = false,
    isParentInput = false,
    defaultValue,
  } = payload;

  const currentTabIndex = findTabIndex(state.tabSections, currentTab);
  if (currentTabIndex === -1) throw new Error('Invalid tab index');

  const sectionIndex = findSectionIndex(state.tabSections[currentTabIndex].sections, focusedSectionName);
  if (sectionIndex === -1) throw new Error('Invalid section index');

  const formItemTabIndex = findTabIndex(state.tabSections[currentTabIndex].sections[sectionIndex].formItemTabs, currentSubTab);
  if (formItemTabIndex === -1) throw new Error('Invalid form item tab index');

  const section = state.tabSections[currentTabIndex].sections[sectionIndex];
  const subTab = section.formItemTabs[formItemTabIndex];

  if (!edit) {
    const sectionItemsNames = subTab.items.map((item) => item.data?.name).filter(Boolean);
    if (sectionItemsNames.includes(name)) {
      toast.error(i18next.t('form.section.error.duplicate'));
      return;
    }
  }

  state.tabSections[currentTabIndex].sections[sectionIndex].formItemTabs[formItemTabIndex].items = subTab.items.map((item) => {
    if (item.x === position.x && item.y === position.y) {
      return {
        x: item.x,
        y: item.y,
        identifier: item.identifier,
        data: {
          disabledName: !!item?.data?.id,
          definition: {
            type,
            [`${toLowerCamelCase(type)}`]: definition,
          },
          name,
          title,
          translatesTitle,
          y: item.y,
          x: item.x,
          isAuxiliary: item.data?.isAuxiliary ?? false,
          defaultValue,
          isActionInput,
          isParentInput,
          visibilities: item.data?.visibilities,
          id,
        },
        isEmpty: false,
      };
    }
    return item;
  });

  section.items = recalcSectionItems(section.formItemTabs);
};
